/**
 *  @swagger
 *  definitions:
 *    RegattaBoatRentParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Name:
 *          description: Name of the RegattaBoatRent
 *          type: string
 *        RegistrationNo:
 *          description: Registration No of the RegattaBoatRent
 *          type: string
 *        Status:
 *          description: Status of the RegattaBoatRent
 *          $ref: '#/components/enums/RegattaBoatRentStatus'
 *        Email:
 *          description: Email of the RegattaBoatRent
 *          type: string
 *        Phone:
 *          description: Phone of the RegattaBoatRent
 *          type: string
 *        Price:
 *          description: Price of the RegattaBoatRent
 *          type: integer
 *        Content:
 *          description: Content of the RegattaBoatRent
 *          type: string
 *        Image:
 *          description: Image of the RegattaBoatRent
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    RegattaBoatRentCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaBoatRentParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Status
 *            - Email
 *            - Price
 *
 *  @swagger
 *  definitions:
 *    RegattaBoatRentQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaBoatRent
 *              type: string
 *        - $ref: '#/definitions/RegattaBoatRentParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaBoatRentUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaBoatRentQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaBoatRent:
 *      allOf:
 *        - $ref: '#/definitions/RegattaBoatRentCreateParams'
 *        - $ref: '#/definitions/RegattaBoatRentUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaBoatRent:
 *        allOf:
 *          - $ref: '#/definitions/RegattaBoatRent'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaBoatRent
 *                $ref: '#/components/schemas/Regatta'
 */

// Enums
import {
  RegattaBoatRentStatus,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Content: null | string;
  Email: string;
  Image: null | string;
  Name: string;
  Phone: null | string;
  Price: number;
  RegistrationNo: null | string;
  Status: RegattaBoatRentStatus;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Content: {
    allowNull: true,
    type: Sequelize.TEXT,
    validate: {
      notEmpty: true,
    },
  },
  Email: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      isEmail: true,
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Image: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Phone: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Price: {
    allowNull: false,
    type: Sequelize.DOUBLE(
      8,
      2,
    ),
  },
  RegistrationNo: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Status: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaBoatRentStatus)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaBoatRent'> => {
  const RegattaBoatRent = stack.define(
    'RegattaBoatRent',
    fields(Sequelize),
    {
      tableName: 'RegattaBoatRents',
    },
  );

  RegattaBoatRent.associate = (models: $ModelClasses) => {
    models.RegattaBoatRent.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
  };

  RegattaBoatRent.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaBoatRent;
});
